/**
 * LoginPage — Supabase Auth Login
 *
 * Branding: Gutachter-Koop-Blau (#0e7490) + offizielles Logo (logo-white.webp).
 * Hinweis: Der Kunden-Kiosk-Button wurde aus dem Login entfernt (Wunsch Inhaber).
 * Die Kiosk-PIN-Logik bleibt im Code erhalten, ist aber vom Login aus nicht mehr verlinkt.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import { Eye, EyeOff, LogIn, Mail, Lock, AlertCircle, CheckCircle2, Loader2, ArrowLeft, Delete } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// SHA-256 Hash-Funktion (Web Crypto API)
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Rate Limiting im localStorage (zusätzlich zur DB)
const RATE_LIMIT_KEY = 'kiosk_pin_attempts';
const RATE_LIMIT_LOCK_KEY = 'kiosk_pin_locked_until';
const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 15 * 60 * 1000; // 15 Minuten

function getRateLimitState(): { attempts: number; lockedUntil: number } {
  const attempts = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
  const lockedUntil = parseInt(localStorage.getItem(RATE_LIMIT_LOCK_KEY) || '0', 10);
  return { attempts, lockedUntil };
}

function incrementAttempts(): number {
  const current = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10) + 1;
  localStorage.setItem(RATE_LIMIT_KEY, String(current));
  if (current >= MAX_ATTEMPTS) {
    const lockUntil = Date.now() + LOCK_DURATION_MS;
    localStorage.setItem(RATE_LIMIT_LOCK_KEY, String(lockUntil));
  }
  return current;
}

function resetAttempts() {
  localStorage.removeItem(RATE_LIMIT_KEY);
  localStorage.removeItem(RATE_LIMIT_LOCK_KEY);
}

export default function LoginPage() {
  const { signIn, resetPassword } = useAuth();
  const [, setLocation] = useLocation();

  // ─── Shared State ─────────────────────────────────────────────────
  const [mode, setMode] = useState<'login' | 'kiosk' | 'reset'>('login');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ─── Login State ──────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // ─── Kiosk PIN State ──────────────────────────────────────────────
  const [pin, setPin] = useState('');
  const [pinBlocked, setPinBlocked] = useState(false);
  const [pinBlockedUntil, setPinBlockedUntil] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [honeypot, setHoneypot] = useState(''); // Bot-Trap
  const [pinSuccess, setPinSuccess] = useState(false);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // ─── Countdown für Blockierung ────────────────────────────────────
  const [countdown, setCountdown] = useState(0);

  // Beim Mount: Rate Limit State aus localStorage laden
  useEffect(() => {
    const { attempts, lockedUntil } = getRateLimitState();
    if (lockedUntil > Date.now()) {
      setPinBlocked(true);
      setPinBlockedUntil(lockedUntil);
      setRemainingAttempts(0);
    } else if (attempts > 0) {
      setRemainingAttempts(Math.max(0, MAX_ATTEMPTS - attempts));
      if (attempts >= MAX_ATTEMPTS) {
        resetAttempts(); // Lock abgelaufen, reset
      }
    }
  }, []);

  useEffect(() => {
    if (!pinBlocked || pinBlockedUntil <= 0) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((pinBlockedUntil - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) {
        setPinBlocked(false);
        setPinBlockedUntil(0);
        setRemainingAttempts(MAX_ATTEMPTS);
        setError(null);
        resetAttempts();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [pinBlocked, pinBlockedUntil]);

  // ─── Google Login ─────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/` }
    });
    if (error) {
      setError('Google-Login fehlgeschlagen: ' + error.message);
      setGoogleLoading(false);
    }
  };

  // ─── E-Mail/Passwort Login ────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Bitte E-Mail und Passwort eingeben.');
      return;
    }
    setLoading(true);
    setError(null);
    const result = await signIn(email, password);
    setLoading(false);
    if (result.error) {
      setError(result.error);
    } else {
      setLocation('/');
    }
  };

  // ─── Passwort Reset ───────────────────────────────────────────────
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Bitte E-Mail-Adresse eingeben.');
      return;
    }
    setLoading(true);
    setError(null);
    const result = await resetPassword(email);
    setLoading(false);
    if (result.error) {
      setError(result.error);
    } else {
      setResetSent(true);
    }
  };

  // ─── Kiosk PIN Validierung (direkt über Supabase REST API) ───────
  const validatePin = useCallback(async (fullPin: string) => {
    if (pinBlocked || loading) return;

    // Honeypot-Check: Wenn ausgefüllt, ist es ein Bot → Fake-Antwort
    if (honeypot) {
      await new Promise(r => setTimeout(r, 1500));
      setError('PIN ungültig');
      setPin('');
      return;
    }

    // Lokales Rate Limiting prüfen
    const { lockedUntil } = getRateLimitState();
    if (lockedUntil > Date.now()) {
      setPinBlocked(true);
      setPinBlockedUntil(lockedUntil);
      setError('Zu viele Versuche. Bitte warten.');
      setPin('');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { attempts } = getRateLimitState();
      if (attempts > 0) {
        const delay = Math.min(500 * Math.pow(2, attempts - 1), 8000);
        await new Promise(r => setTimeout(r, delay));
      }

      const pinHash = await sha256(fullPin);

      const { data, error: dbError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'kiosk_pin')
        .single();

      if (dbError || !data) {
        setError('Kiosk ist nicht konfiguriert. Bitte Admin kontaktieren.');
        setPin('');
        setLoading(false);
        return;
      }

      const storedHash = data.value;
      const isValid = pinHash.length === storedHash.length &&
        pinHash.split('').every((c: string, i: number) => c === storedHash[i]);

      if (isValid) {
        setPinSuccess(true);
        setError(null);
        resetAttempts();
        sessionStorage.setItem('kiosk_access', 'true');
        sessionStorage.setItem('kiosk_timestamp', String(Date.now()));
        setTimeout(() => {
          setLocation('/kiosk/kunden');
        }, 800);
      } else {
        const newCount = incrementAttempts();
        const remaining = Math.max(0, MAX_ATTEMPTS - newCount);
        setRemainingAttempts(remaining);

        if (remaining <= 0) {
          setPinBlocked(true);
          const lockUntil = Date.now() + LOCK_DURATION_MS;
          setPinBlockedUntil(lockUntil);
          setCountdown(Math.ceil(LOCK_DURATION_MS / 1000));
          setError('Zu viele Fehlversuche. 15 Minuten gesperrt.');
        } else {
          setError(`PIN ungültig. Noch ${remaining} Versuch${remaining !== 1 ? 'e' : ''}.`);
        }
        setPin('');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte erneut versuchen.');
      setPin('');
    } finally {
      setLoading(false);
    }
  }, [pinBlocked, loading, honeypot, setLocation]);

  // ─── PIN Numpad Handler ───────────────────────────────────────────
  const handleNumpadPress = (digit: string) => {
    if (pinBlocked || loading || pinSuccess) return;
    if (pin.length >= 6) return;

    const newPin = pin + digit;
    setPin(newPin);
    setError(null);

    if (newPin.length === 6) {
      validatePin(newPin);
    }
  };

  const handleNumpadDelete = () => {
    if (loading || pinSuccess) return;
    setPin(prev => prev.slice(0, -1));
    setError(null);
  };

  const handleNumpadClear = () => {
    if (loading || pinSuccess) return;
    setPin('');
    setError(null);
  };

  // ─── Mode Switch Handler ──────────────────────────────────────────
  const switchMode = (newMode: 'login' | 'kiosk' | 'reset') => {
    setMode(newMode);
    setError(null);
    setPin('');
    setPinSuccess(false);
  };

  // ─── RENDER ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-[420px]">
        {/* Logo + Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#0e7490] shadow-lg mb-4 p-3">
            <img
              src="https://gutachter-koop.de/images/logo-white.webp"
              alt="Gutachter Koop"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Gutachter KOOP</h1>
          <p className="text-sm text-gray-500 mt-1">Firmengruppe — Internes Verwaltungssystem</p>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MODE: KIOSK PIN (nur per Direkt-Link erreichbar) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {mode === 'kiosk' && (
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => switchMode('login')}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-gray-500" />
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Kunden-Kiosk</h2>
                <p className="text-xs text-gray-500">6-stelligen PIN eingeben</p>
              </div>
            </div>

            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all duration-200 ${
                    pinSuccess
                      ? 'bg-green-500 scale-110'
                      : i < pin.length
                        ? 'bg-[#0e7490] scale-110'
                        : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            {pinSuccess && (
              <div className="flex items-center justify-center gap-2 mb-4 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Zugang gewährt</span>
              </div>
            )}

            {loading && !pinSuccess && (
              <div className="flex items-center justify-center gap-2 mb-4 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Wird geprüft...</span>
              </div>
            )}

            {error && !pinSuccess && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100 mb-4">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-xs text-red-700">{error}</span>
              </div>
            )}

            {pinBlocked && countdown > 0 && (
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    Gesperrt — {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
                  </span>
                </div>
              </div>
            )}

            {!pinSuccess && (
              <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => (
                  <button
                    key={digit}
                    onClick={() => handleNumpadPress(digit)}
                    disabled={pinBlocked || loading}
                    className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-semibold text-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed select-none"
                  >
                    {digit}
                  </button>
                ))}
                <button
                  onClick={handleNumpadClear}
                  disabled={pinBlocked || loading || pin.length === 0}
                  className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xs font-medium text-gray-500 transition-all disabled:opacity-30 select-none"
                >
                  Löschen
                </button>
                <button
                  onClick={() => handleNumpadPress('0')}
                  disabled={pinBlocked || loading}
                  className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-semibold text-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed select-none"
                >
                  0
                </button>
                <button
                  onClick={handleNumpadDelete}
                  disabled={pinBlocked || loading || pin.length === 0}
                  className="h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center text-gray-500 transition-all disabled:opacity-30 select-none"
                >
                  <Delete className="w-5 h-5" />
                </button>
              </div>
            )}

            {!pinBlocked && !pinSuccess && remainingAttempts < MAX_ATTEMPTS && (
              <p className="text-center text-xs text-gray-400 mt-4">
                Noch {remainingAttempts} Versuch{remainingAttempts !== 1 ? 'e' : ''}
              </p>
            )}

            <input
              type="text"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
            />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MODE: PASSWORT VERGESSEN */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {mode === 'reset' && (
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Passwort zurücksetzen</h2>
            <p className="text-sm text-gray-500 mb-6">
              Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen.
            </p>

            {resetSent ? (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-100">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">E-Mail gesendet</p>
                  <p className="text-xs text-green-600 mt-1">
                    Prüfen Sie Ihr Postfach ({email}) und folgen Sie dem Link.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail-Adresse</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@gutachter-koop.de"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0e7490]/20 focus:border-[#0e7490] transition-all"
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-xs text-red-700">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0e7490] hover:bg-[#155e75] text-white font-medium text-sm transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Link senden
                </button>
              </form>
            )}

            <button
              onClick={() => { switchMode('login'); setResetSent(false); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-4 transition-colors"
            >
              Zurück zum Login
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MODE: MITARBEITER LOGIN */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {mode === 'login' && (
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Anmelden</h2>
            <p className="text-sm text-gray-500 mb-6">
              Melden Sie sich mit Ihren Zugangsdaten an.
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail-Adresse</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@gutachter-koop.de"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0e7490]/20 focus:border-[#0e7490] transition-all"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Passwort</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0e7490]/20 focus:border-[#0e7490] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0e7490] hover:bg-[#155e75] text-white font-medium text-sm transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                Anmelden
              </button>
            </form>

            {/* Google Login */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">oder</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading || loading}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors disabled:opacity-50 shadow-sm"
            >
              {googleLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Mit Google anmelden
            </button>

            {/* Passwort vergessen */}
            <button
              onClick={() => switchMode('reset')}
              className="w-full text-center text-sm text-gray-500 hover:text-[#0e7490] mt-4 transition-colors"
            >
              Passwort vergessen?
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          SV Büro Koop &middot; LCS GmbH &middot; Gutachter KOOP Firmengruppe
        </p>
      </div>
    </div>
  );
}
